<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.UI.6 — "Conectar proveedor de identidad", step 1 of 2
// (canvas: SsoConnect). Saving sends the connection back to draft when its
// identity changes; the parent then runs the test (step 2).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import KaptionSecurityCopyRow from './KaptionSecurityCopyRow.vue';

const emit = defineEmits(['saved']);
const { t } = useI18n();
const { overview, setOverview, connection } = useKaptionSecurity();

const PROVIDERS = ['auth0', 'okta', 'entra', 'google', 'oidc', 'saml'];

const dialogRef = ref(null);
const picked = ref('auth0');
const issuer = ref('');
const clientId = ref('');
const clientSecret = ref('');
const metadataUrl = ref('');
const metadataXml = ref('');
const entityId = ref('');
const ssoUrl = ref('');
const certificate = ref('');
const showManual = ref(false);
const busy = ref(false);
const error = ref('');

const isSaml = computed(() => picked.value === 'saml');
const providerName = computed(() =>
  t(`KAPTION_SECURITY.CONNECT.PROVIDERS.${picked.value}`)
);
const urls = computed(() => overview.value?.urls || {});
const hasSecret = computed(
  () =>
    !!connection.value?.has_client_secret &&
    connection.value?.protocol === 'oidc'
);

const copyRows = computed(() =>
  isSaml.value
    ? [
        {
          label: t('KAPTION_SECURITY.CONNECT.ACS'),
          value: urls.value.saml_acs,
        },
        {
          label: t('KAPTION_SECURITY.CONNECT.ENTITY'),
          value: urls.value.saml_entity_id,
        },
        {
          label: t('KAPTION_SECURITY.CONNECT.METADATA'),
          value: urls.value.saml_metadata,
        },
      ]
    : [
        {
          label: t('KAPTION_SECURITY.CONNECT.CALLBACK'),
          value: urls.value.oidc_callback,
        },
        {
          label: t('KAPTION_SECURITY.CONNECT.LOGOUT'),
          value: urls.value.logout,
        },
      ]
);

const open = () => {
  const c = connection.value;
  let provider = c?.provider || 'auth0';
  if (c?.protocol === 'saml') provider = 'saml';
  picked.value = provider;
  if (!PROVIDERS.includes(picked.value)) picked.value = 'oidc';
  issuer.value = c?.issuer_input || c?.issuer || '';
  clientId.value = c?.client_id || '';
  clientSecret.value = '';
  metadataUrl.value = c?.saml_metadata_url || '';
  metadataXml.value = '';
  entityId.value = c?.saml_idp_entity_id || '';
  ssoUrl.value = c?.saml_idp_sso_url || '';
  certificate.value = '';
  showManual.value = false;
  error.value = '';
  dialogRef.value?.open();
};

const payload = () =>
  isSaml.value
    ? {
        protocol: 'saml',
        provider: 'saml',
        saml_metadata_url: metadataUrl.value.trim() || undefined,
        saml_metadata_xml: metadataXml.value.trim() || undefined,
        saml_idp_entity_id: entityId.value.trim() || undefined,
        saml_idp_sso_url: ssoUrl.value.trim() || undefined,
        saml_idp_certificate: certificate.value.trim() || undefined,
      }
    : {
        protocol: 'oidc',
        provider: picked.value,
        issuer_input: issuer.value.trim(),
        client_id: clientId.value.trim(),
        client_secret: clientSecret.value || undefined,
      };

const canSave = computed(() => {
  if (isSaml.value)
    return !!(
      metadataUrl.value ||
      metadataXml.value ||
      (entityId.value && ssoUrl.value)
    );
  return !!(
    issuer.value &&
    clientId.value &&
    (clientSecret.value || hasSecret.value)
  );
});

const errorText = data => {
  const code = data?.error_code;
  const known = [
    'discovery_failed',
    'discovery_invalid',
    'issuer_mismatch',
    'metadata_fetch_failed',
    'metadata_invalid',
    'metadata_incomplete',
    'blocked_address',
  ];
  if (known.includes(code)) return t(`KAPTION_SECURITY.CONNECT.ERRORS.${code}`);
  return data?.error || t('KAPTION_SECURITY.CONNECT.ERRORS.generic');
};

const save = async () => {
  if (!canSave.value) return;
  busy.value = true;
  error.value = '';
  // Open the test window now, on the click: opened after the save it would be
  // blocked. The test dialog then navigates this same named window.
  const testWindow = window.open(
    'about:blank',
    'kaption-sso-test',
    'width=520,height=720'
  );
  try {
    const { data } = await KaptionSecurityAPI.saveConnection(payload());
    setOverview(data);
    dialogRef.value?.close();
    emit('saved');
  } catch (e) {
    testWindow?.close();
    error.value = errorText(e?.response?.data);
  } finally {
    busy.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KAPTION_SECURITY.CONNECT.TITLE')"
    :description="t('KAPTION_SECURITY.CONNECT.STEP')"
    :confirm-button-label="t('KAPTION_SECURITY.CONNECT.SAVE_AND_TEST')"
    :disable-confirm-button="!canSave || busy"
    :is-loading="busy"
    width="2xl"
    overflow-y-auto
    @confirm="save"
  >
    <div class="flex flex-col gap-5">
      <div
        role="radiogroup"
        :aria-label="t('KAPTION_SECURITY.CONNECT.PROVIDERS_LABEL')"
        class="grid grid-cols-2 gap-2 sm:grid-cols-3"
      >
        <button
          v-for="id in PROVIDERS"
          :key="id"
          type="button"
          role="radio"
          :aria-checked="picked === id"
          class="flex flex-col items-start gap-0.5 px-3 py-2.5 text-left rounded-xl"
          :class="
            picked === id
              ? 'outline outline-2 outline-n-brand bg-n-brand/5'
              : 'outline outline-1 outline-n-weak bg-n-background hover:bg-n-alpha-1'
          "
          :data-testid="`ks-provider-${id}`"
          @click="picked = id"
        >
          <span class="text-sm font-semibold text-n-slate-12">{{
            t(`KAPTION_SECURITY.CONNECT.PROVIDERS.${id}`)
          }}</span>
          <span class="text-xs text-n-slate-11">
            {{
              id === 'saml'
                ? t('KAPTION_SECURITY.CONNECT.PROTO_SAML')
                : t('KAPTION_SECURITY.CONNECT.PROTO_OIDC')
            }}
          </span>
        </button>
      </div>

      <div v-if="!isSaml" class="flex flex-col gap-4">
        <div class="flex flex-col gap-1.5">
          <label for="ks-issuer" class="text-sm font-medium text-n-slate-12">{{
            t(`KAPTION_SECURITY.CONNECT.ISSUER_LABELS.${picked}`)
          }}</label>
          <input
            id="ks-issuer"
            v-model="issuer"
            type="text"
            autocomplete="off"
            class="ks-input"
            :placeholder="
              t(`KAPTION_SECURITY.CONNECT.ISSUER_PLACEHOLDERS.${picked}`)
            "
          />
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <label
              for="ks-client-id"
              class="text-sm font-medium text-n-slate-12"
              >{{ t('KAPTION_SECURITY.CONNECT.CLIENT_ID') }}</label
            >
            <input
              id="ks-client-id"
              v-model="clientId"
              type="text"
              autocomplete="off"
              class="ks-input"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label
              for="ks-client-secret"
              class="text-sm font-medium text-n-slate-12"
              >{{ t('KAPTION_SECURITY.CONNECT.CLIENT_SECRET') }}</label
            >
            <input
              id="ks-client-secret"
              v-model="clientSecret"
              type="password"
              autocomplete="new-password"
              class="ks-input"
              :placeholder="hasSecret ? '••••••••' : ''"
            />
            <span v-if="hasSecret" class="text-xs text-n-slate-11">{{
              t('KAPTION_SECURITY.CONNECT.SECRET_KEEP')
            }}</span>
          </div>
        </div>
      </div>

      <div v-else class="flex flex-col gap-3">
        <div class="flex flex-col gap-1.5">
          <label
            for="ks-metadata"
            class="text-sm font-medium text-n-slate-12"
            >{{ t('KAPTION_SECURITY.CONNECT.METADATA_URL') }}</label
          >
          <input
            id="ks-metadata"
            v-model="metadataUrl"
            type="url"
            autocomplete="off"
            class="ks-input"
            :placeholder="
              t('KAPTION_SECURITY.CONNECT.ISSUER_PLACEHOLDERS.oidc')
            "
          />
        </div>
        <p class="m-0 text-[13px] text-n-slate-11">
          {{ t('KAPTION_SECURITY.CONNECT.METADATA_HELP') }}
          <button
            type="button"
            class="font-medium text-n-brand"
            @click="showManual = !showManual"
          >
            {{ t('KAPTION_SECURITY.CONNECT.SHOW_MANUAL') }}
          </button>
        </p>
        <template v-if="showManual">
          <div class="flex flex-col gap-1.5">
            <label
              for="ks-metadata-xml"
              class="text-sm font-medium text-n-slate-12"
              >{{ t('KAPTION_SECURITY.CONNECT.METADATA_XML') }}</label
            >
            <textarea
              id="ks-metadata-xml"
              v-model="metadataXml"
              rows="3"
              class="ks-input !h-auto py-2 font-mono text-xs"
            />
          </div>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <label
                for="ks-entity"
                class="text-sm font-medium text-n-slate-12"
                >{{ t('KAPTION_SECURITY.CONNECT.ENTITY_ID') }}</label
              >
              <input
                id="ks-entity"
                v-model="entityId"
                type="text"
                class="ks-input"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label
                for="ks-sso-url"
                class="text-sm font-medium text-n-slate-12"
                >{{ t('KAPTION_SECURITY.CONNECT.SSO_URL') }}</label
              >
              <input
                id="ks-sso-url"
                v-model="ssoUrl"
                type="url"
                class="ks-input"
              />
            </div>
          </div>
          <div class="flex flex-col gap-1.5">
            <label for="ks-cert" class="text-sm font-medium text-n-slate-12">{{
              t('KAPTION_SECURITY.CONNECT.CERTIFICATE')
            }}</label>
            <textarea
              id="ks-cert"
              v-model="certificate"
              rows="3"
              class="ks-input !h-auto py-2 font-mono text-xs"
            />
            <span
              v-if="connection?.has_certificate"
              class="text-xs text-n-slate-11"
              >{{ t('KAPTION_SECURITY.CONNECT.CERTIFICATE_SAVED') }}</span
            >
          </div>
        </template>
      </div>

      <div
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <span class="text-sm font-semibold text-n-slate-12">
          {{
            isSaml
              ? t('KAPTION_SECURITY.CONNECT.COPY_TITLE_SAML')
              : t('KAPTION_SECURITY.CONNECT.COPY_TITLE', {
                  provider: providerName,
                })
          }}
        </span>
        <KaptionSecurityCopyRow
          v-for="row in copyRows"
          :key="row.label"
          :label="row.label"
          :value="row.value || ''"
        />
        <p class="m-0 text-[13px] text-n-slate-11">
          {{ t(`KAPTION_SECURITY.CONNECT.GUIDES.${picked}`) }}
        </p>
      </div>

      <p
        v-if="error"
        class="px-3 py-2 m-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
        data-testid="ks-connect-error"
      >
        {{ error }}
      </p>
    </div>
  </Dialog>
</template>

<style scoped>
.ks-input {
  @apply h-10 w-full px-3 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12 outline-none focus:border-n-brand;
}
</style>
